import React from 'react';
import { useTranslation } from 'react-i18next';
import { LANGUAGES, isRTL } from '../../i18n';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { Globe, Check } from 'lucide-react';

export const LanguageSwitcher = ({ variant = 'default', className = '' }) => {
  const { i18n, t } = useTranslation();
  const currentLang = LANGUAGES.find(lang => lang.code === i18n.language) || LANGUAGES[0];

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
  };

  // Compact variant for navbar
  if (variant === 'compact') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className={`flex items-center gap-1.5 px-2 ${className}`}
            data-testid="language-switcher-btn"
          >
            <Globe className="w-4 h-4" />
            <span className="text-sm font-medium">{currentLang.flag}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          {LANGUAGES.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              onSelect={() => handleLanguageChange(lang.code)}
              className={`flex items-center justify-between cursor-pointer ${
                i18n.language === lang.code ? 'bg-continental/5' : ''
              }`}
              data-testid={`lang-option-${lang.code}`}
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">{lang.flag}</span>
                <span className={isRTL(lang.code) ? 'font-arabic' : ''}>
                  {lang.nativeName}
                </span>
              </div>
              {i18n.language === lang.code && (
                <Check className="w-4 h-4 text-continental" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Default full variant
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          className={`flex items-center gap-2 ${className}`}
          data-testid="language-switcher-btn"
        >
          <Globe className="w-4 h-4" />
          <span>{currentLang.flag}</span>
          <span className={isRTL(currentLang.code) ? 'font-arabic' : ''}>
            {currentLang.nativeName}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <div className="px-2 py-1.5 text-sm font-medium text-gray-500 border-b mb-1">
          {t('language.select')}
        </div>
        {LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onSelect={() => handleLanguageChange(lang.code)}
            className={`flex items-center justify-between cursor-pointer ${
              i18n.language === lang.code ? 'bg-continental/5' : ''
            }`}
            data-testid={`lang-option-${lang.code}`}
          >
            <div className="flex items-center gap-3">
              <span className="text-xl">{lang.flag}</span>
              <div>
                <p className={`font-medium ${isRTL(lang.code) ? 'font-arabic' : ''}`}>
                  {lang.nativeName}
                </p>
                <p className="text-xs text-gray-500">{lang.name}</p>
              </div>
            </div>
            {i18n.language === lang.code && (
              <Check className="w-4 h-4 text-continental" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
